import { useCallback, useEffect, useMemo, useRef } from 'react';

import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom';
import { DragDropProvider } from '@dnd-kit/react';
import { useBlocker } from 'react-router';
import { Backdrop, Box, CircularProgress } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import { useDeleteDashboard, useSaveDashboard } from '@/features/dashboard/api/mutations';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import {
  DrawerStep,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { TAB_DEFAULT_BASE_NAME } from '@/features/dashboard/constants/names';
import AppsOrder from '@/features/dashboard/pages/Edit/components/AppsOrder';
import { EditBanner } from '@/features/dashboard/pages/Edit/components/EditBanner';
import EditDashboardName from '@/features/dashboard/pages/Edit/components/EditDashboardName';
import { TilesOrder } from '@/features/dashboard/pages/Edit/components/TilesOrder';
import { buildPreviewConfig } from '@/features/dashboard/state/lib/mapping';
import { RemoveWidgetProvider } from '@/features/dashboard/state/RemoveWidgetContext';
import { useMarketPreview } from '@/features/markets';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';
import presets from '@/shared/ui/overlays/Modal/presets';
import { useAppSelector } from '@/store/hooks';

import {
  selectCurrentMarketTabApps,
  selectCurrentTabIndex,
  selectCurrentTiles,
  selectDashboardName,
  selectDraftTabNames,
  selectDraftTabs,
  selectHasChanges,
  selectHasTabNameErrors,
  selectInitialDashboardName
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { DashboardLayout } from '../../components/DashboardLayout';
import { SaveErrorDialog } from '../../components/SaveErrorDialog';
import { ConfirmationDialog, EditableTabLabel, EditActionButtons } from './components';
import { useDashboardEditActions } from './hooks/useDashboardEditActions';
import { useDashboardEditInit } from './hooks/useDashboardEditInit';
import { useDashboardEditModals } from './hooks/useDashboardEditModals';
import { useTabDragReordering } from './hooks/useTabDragReordering';

import type { DashboardTab, MarketApps } from '../../types';

export const EditDashboard = ({
  onClose,
  dashboardName: currentDashboardName,
  initialDashboardName: initialNameProp,
  dashboardConfig,
  activeTab = 0
}: {
  onClose: () => void;
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  dashboardStructure?: DashboardTab | null;
  activeTab?: number;
}) => {
  const { isInitialized, isLoading: isInitLoading } = useDashboardEditInit({
    dashboardName: currentDashboardName,
    initialDashboardName: initialNameProp,
    dashboardConfig,
    activeTabIndex: activeTab
  });

  const draftTabs = useAppSelector(selectDraftTabs);
  const draftTabNames = useAppSelector(selectDraftTabNames);
  const hasChanges = useAppSelector(selectHasChanges);
  const hasTabNameErrors = useAppSelector(selectHasTabNameErrors);
  const currentTabIndex = useAppSelector(selectCurrentTabIndex);
  const currentTiles = useAppSelector(selectCurrentTiles);
  const currentMarketTabApps = useAppSelector(selectCurrentMarketTabApps);

  const previewConfig = useMemo(
    () => buildPreviewConfig(currentTiles, currentMarketTabApps),
    [currentTiles, currentMarketTabApps]
  );
  const { data: previewData } = useMarketPreview(previewConfig);

  const { renameTab, addTab, deleteTab, reorderTabs, setCurrentTab, resetToEmpty } =
    useDashboardEditActions();

  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const draftDashboardName = useAppSelector(selectDashboardName);
  const isRename = draftDashboardName !== initialDashboardName && currentDashboardName !== 'new';
  const saveNavigationRef = useRef(false);
  const { mutate: saveMutate, isPending: isSaving, isSuccess: isSaveSuccess } = useSaveDashboard();
  const { mutate: deleteDashboard, isPending: isDeletingDashboard } =
    useDeleteDashboard(initialDashboardName);

  const { setMode } = useAppForm();
  useEffect(() => {
    setMode('edit');
  }, [setMode]);

  useEffect(() => {
    if (isSaveSuccess) {
      onClose();
    }
  }, [isSaveSuccess, onClose]);

  const {
    discardDialog,
    removeAllDialog,
    removeDashboardDialog,
    removeWidgetDialog,
    saveErrorDialog,
    hasDashboardNameErrors,
    setHasDashboardNameErrors,
    handleRemoveAll,
    handleRemoveDashboard,
    openRemoveModal,
    handleDiscardClick,
    handleConfirmRemoveWidget
  } = useDashboardEditModals({
    hasChanges,
    onClose,
    resetToEmpty,
    deleteDashboard
  });

  const { getTabHandleRef, handleTabDragEnd, handleTabCreated } = useTabDragReordering({
    draftTabs,
    reorderTabs,
    addTab
  });

  const isSaveDisabled = isSaving || !hasChanges || hasDashboardNameErrors || hasTabNameErrors;

  const handleSave = useCallback(() => {
    saveNavigationRef.current = true;
    saveMutate(undefined, {
      onError: () => {
        saveNavigationRef.current = false;
        saveErrorDialog.open();
      }
    });
  }, [saveMutate, saveErrorDialog]);

  const handleSaveRetry = useCallback(() => {
    saveErrorDialog.close();
    handleSave();
  }, [saveErrorDialog, handleSave]);

  const handleSaveDiscard = useCallback(() => {
    saveErrorDialog.close();
    onClose();
  }, [saveErrorDialog, onClose]);

  const tabsConfig = useMemo(() => {
    return draftTabs.map(tab => {
      const handleRef = getTabHandleRef(tab.id);
      return {
        id: tab.id,
        name: tab.name,
        sortableHandleRef: handleRef,
        label: (
          <EditableTabLabel
            allowTabReorder
            canDelete={draftTabs.length > 1}
            dragHandleRef={handleRef}
            name={tab.name}
            tabId={tab.id}
            onDelete={deleteTab}
            onRename={renameTab}
          />
        )
      };
    });
  }, [draftTabs, deleteTab, renameTab, getTabHandleRef]);

  const handleAddTabDraft = useCallback(() => {
    addTab(crypto.randomUUID(), TAB_DEFAULT_BASE_NAME);
  }, [addTab]);

  const handleDraftTabChange = useCallback(
    (index: number) => {
      const tabId = draftTabs[index]?.id;
      if (tabId) setCurrentTab(tabId);
    },
    [draftTabs, setCurrentTab]
  );

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();

  const handleAddAppClick = useCallback(() => {
    setIsEditMode(true);
    setEditingTarget(null);
    setDrawerStep(DrawerStep.SelectApp);
  }, [setDrawerStep, setEditingTarget, setIsEditMode]);

  const removeWidgetValue = useMemo(() => ({ openRemoveModal }), [openRemoveModal]);

  const blocker = useBlocker(() => {
    return (
      !saveNavigationRef.current &&
      !isDeletingDashboard &&
      hasChanges &&
      !isSaveSuccess &&
      !isSaving
    );
  });

  const handleBlockerConfirm = useCallback(() => {
    onClose();
    blocker.proceed?.();
  }, [blocker, onClose]);

  const handleBlockerCancel = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  if (!isInitialized || isInitLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <RemoveWidgetProvider value={removeWidgetValue}>
      <Backdrop
        open={isDeletingDashboard}
        sx={{ zIndex: Z_INDEX.loadingBackdrop }}
      >
        <CircularProgress />
      </Backdrop>
      <ConfirmationDialog
        open={discardDialog.isOpen}
        {...presets.discard}
        onCancel={discardDialog.close}
        onConfirm={() => {
          onClose();
          discardDialog.close();
        }}
      />
      <ConfirmationDialog
        open={removeWidgetDialog.isOpen}
        {...presets.removeWidget}
        onCancel={removeWidgetDialog.close}
        onConfirm={handleConfirmRemoveWidget}
      />
      <ConfirmationDialog
        open={removeAllDialog.isOpen}
        {...presets.removeAll}
        onCancel={removeAllDialog.close}
        onConfirm={handleRemoveAll}
      />
      <ConfirmationDialog
        open={removeDashboardDialog.isOpen}
        {...presets.removeDashboard}
        onCancel={removeDashboardDialog.close}
        onConfirm={() => {
          handleRemoveDashboard();
          removeDashboardDialog.close();
        }}
      />
      <ConfirmationDialog
        open={blocker.state === 'blocked'}
        {...presets.discard}
        onCancel={handleBlockerCancel}
        onConfirm={handleBlockerConfirm}
      />
      <SaveErrorDialog
        isNew={currentDashboardName === 'new'}
        isRename={isRename}
        open={saveErrorDialog.isOpen}
        onCancel={saveErrorDialog.close}
        onDiscard={handleSaveDiscard}
        onRetry={handleSaveRetry}
      />
      <DragDropProvider
        plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
        onDragEnd={handleTabDragEnd}
      >
        <DashboardLayout
          disableTabRipple
          activeTab={currentTabIndex}
          minTabWidth={50}
          renderRight={
            <EditActionButtons
              isPending={isDeletingDashboard}
              isSaveDisabled={isSaveDisabled}
              onAddApp={handleAddAppClick}
              onDiscard={handleDiscardClick}
              onRemoveAll={removeAllDialog.open}
              onRemoveDashboard={removeDashboardDialog.open}
              onSave={handleSave}
            />
          }
          tabNames={draftTabNames}
          tabsConfig={tabsConfig}
          title={<EditDashboardName onValidationChange={setHasDashboardNameErrors} />}
          onAddTab={handleAddTabDraft}
          onTabChange={handleDraftTabChange}
        >
          <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <EditBanner />
              <TilesOrder preview={previewData} />
              <AppsOrder
                preview={previewData}
                onTabCreated={handleTabCreated}
              />
            </Box>
          </PageInformationContext>
        </DashboardLayout>
      </DragDropProvider>
    </RemoveWidgetProvider>
  );
};
